import { API_BASE_URL } from '../config/apiConfig';

/** Decode a JWT payload without external dependencies */
function decodeJwtPayload(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  phone?: string;
  picture?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserAuthData {
  email: string;
  password?: string;
  firstName: string;
  lastName: string;
  name?: string;
  phone?: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  userId?: string;
  token?: string;
  user?: User;
  error?: string;
}

class UserAuthService {
  private baseUrl = API_BASE_URL;

  private listeners: ((user: User | null) => void)[] = [];

  private async notifyListeners(user: User | null) {
    this.listeners.forEach(listener => listener(user));
  }

  /**
   * Register a new user - PRIMARY: CUSTOM BACKEND
   */
  async registerUser(userData: UserAuthData): Promise<AuthResponse> {
    try {
      if (!userData.name) {
        userData.name = `${userData.firstName} ${userData.lastName}`.trim();
      }

      const response = await fetch(`${this.baseUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userData.email,
          password: userData.password,
          name: userData.name,
          phone: userData.phone
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || data.error?.message || 'Registration failed');
      }

      const resultData = data.data || data;

      const user = resultData.user ? this.mapBackendUser(resultData.user) : undefined;

      if (user && resultData.token) {
        this.notifyListeners(user);
      }

      return {
        success: true,
        message: 'Registration successful',
        userId: resultData.user?.id || resultData.id || resultData.userId,
        token: resultData.token,
        user
      };
    } catch (error: any) {
      console.error('Registration error:', error);
      return {
        success: false,
        message: error.message || 'Registration failed',
      };
    }
  }

  /**
   * Login user - PRIMARY: CUSTOM BACKEND
   */
  async loginUser(email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || data.error?.message || 'Login failed');
      }

      const resultData = data.data || data;

      if (resultData.token) {
        localStorage.setItem('authToken', resultData.token);
        // Strict isolation: clear adminAuth from localStorage when regular user logs in
        localStorage.removeItem('adminAuth');
      }

      const user = resultData.user ? this.mapBackendUser(resultData.user) : undefined;

      if (user) {
        this.notifyListeners(user);
      }

      return {
        success: true,
        message: 'Login successful',
        userId: resultData.user?.id || resultData.id || resultData.userId,
        token: resultData.token,
        user
      };
    } catch (error: any) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.message || 'Login failed',
      };
    }
  }

  /**
   * Custom Google Login - hits OWN Backend
   */
  async googleLogin(token: string, tokenType: 'idToken' | 'accessToken' = 'idToken'): Promise<AuthResponse> {
    try {
      let payloadToSend: any = {};

      if (tokenType === 'idToken' || (token.includes('.') && token.split('.').length === 3)) {
        // It's likely a JWT ID token, decode it to send profile directly to the backend
        try {
          const decoded: any = decodeJwtPayload(token);
          if (decoded.picture) {
            localStorage.setItem('userAvatar', decoded.picture);
          }
          payloadToSend = {
            email: decoded.email,
            name: decoded.name || `${decoded.given_name || ''} ${decoded.family_name || ''}`.trim(),
            sub: decoded.sub,
            googleId: decoded.sub
          };
        } catch (e) {
          console.warn("Could not decode Google token:", e);
          payloadToSend = { idToken: token, tokenType };
        }
      } else if (tokenType === 'accessToken') {
        // Fetch user profile using access token
        try {
          const profileResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!profileResponse.ok) {
            throw new Error('Failed to fetch Google profile');
          }

          const profile = await profileResponse.json();
          if (profile.picture) {
            localStorage.setItem('userAvatar', profile.picture);
          }
          payloadToSend = {
            email: profile.email,
            name: profile.name || `${profile.given_name || ''} ${profile.family_name || ''}`.trim(),
            sub: profile.sub,
            googleId: profile.sub
          };
        } catch (e) {
          console.error("Error fetching Google profile:", e);
          throw new Error('Failed to retrieve user profile from Google');
        }
      } else {
        payloadToSend = { idToken: token, tokenType };
      }

      const response = await fetch(`${this.baseUrl}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        // We now send the decoded user info OR the token itself depending on what the frontend successfully grabbed
        body: JSON.stringify(payloadToSend),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Google login failed');
      }

      const resultData = data.data || data;

      if (resultData.token) {
        localStorage.setItem('authToken', resultData.token);
        // Strict isolation: clear adminAuth from localStorage when regular user logs in via Google
        localStorage.removeItem('adminAuth');
      }

      const user = resultData.user ? this.mapBackendUser(resultData.user) : undefined;

      if (user) {
        this.notifyListeners(user);
      }

      return {
        success: true,
        message: 'Google login successful',
        userId: resultData.user?.id || resultData.id || resultData.userId,
        token: resultData.token,
        user
      };
    } catch (error: any) {
      console.error('Google login error:', error);
      return {
        success: false,
        message: error.message || 'Google login failed',
      };
    }
  }

  async verifyToken(token: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Token verification error:', error);
      return false;
    }
  }

  getStoredToken(): string | null {
    return localStorage.getItem('authToken');
  }

  clearStoredToken(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('userAvatar');
  }

  async logout(): Promise<void> {
    this.clearStoredToken();
    this.notifyListeners(null);
  }

  /**
   * Get current user - Custom Backend Token Auth Mode
   */
  async getCurrentUser(): Promise<User | null> {
    const token = this.getStoredToken();

    if (!token) {
      return null;
    }

    try {
      const response = await fetch(`${this.baseUrl}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const userData = data.data || data;
        const user = this.mapBackendUser(userData);
        return user;
      } else {
        console.warn(`⚠️ Session verification failed: ${response.status} ${response.statusText}`);
        // Only clear token if explicitly unauthorized
        if (response.status === 401 || response.status === 403) {
          this.clearStoredToken();
          this.notifyListeners(null);
        }
      }
    } catch (error) {
      console.error('❌ Network error checking session:', error);
      // Do not clear token on network error
    }

    return null;
  }

  private mapBackendUser(backendUser: any): User {
    const storedAvatar = localStorage.getItem('userAvatar');
    return {
      id: backendUser.id,
      email: backendUser.email,
      firstName: backendUser.firstName || backendUser.name?.split(' ')[0] || '',
      lastName: backendUser.lastName || backendUser.name?.split(' ').slice(1).join(' ') || '',
      name: backendUser.name || `${backendUser.firstName} ${backendUser.lastName}`.trim(),
      phone: backendUser.phone,
      picture: backendUser.avatarUrl || backendUser.picture || storedAvatar || undefined,
      createdAt: backendUser.createdAt || backendUser.created_at || new Date().toISOString(),
      updatedAt: backendUser.updatedAt || backendUser.updated_at || new Date().toISOString()
    };
  }

  async signUp(email: string, password: string, name: string, phone?: string): Promise<AuthResponse> {
    const userData: UserAuthData = {
      email,
      password,
      name,
      firstName: name.split(' ')[0] || name,
      lastName: name.split(' ').slice(1).join(' ') || '',
      phone
    };
    return this.registerUser(userData);
  }

  async signIn(email: string, password: string): Promise<AuthResponse> {
    return this.loginUser(email, password);
  }

  onAuthStateChange(callback: (user: User | null) => void) {
    this.listeners.push(callback);
    // Initial check
    this.getCurrentUser().then(user => {
      // Only callback if it hasn't changed in the meantime?
      // Actually, just calling it is fine, but AuthContext calls getCurrentUser explicitly too.
      // Let's rely on AuthContext's explicit init to avoid race conditions, or let this be redundant but safe.
      // However, we MUST notify if we found a user!
      if (user) callback(user);
    });

    return {
      unsubscribe: () => {
        this.listeners = this.listeners.filter(l => l !== callback);
      }
    };
  }

  async signOut(): Promise<void> {
    console.group('🚪 Logout Triggered');
    console.trace('Who called logout?');
    console.groupEnd();
    return this.logout();
  }
}

export const userAuthService = new UserAuthService();